import { ReactNode, useEffect, useRef, useState } from 'react';
import { prepareForTransition } from '../utils/dom';
import styles from './ExpandySection.module.css';

export interface Props {
  headerContent: ReactNode;
  children: ReactNode;
  initiallyExpanded?: boolean;
  containerClassName?: string;
  expandedContainerClassName?: string;
  expandButtonAlignment?: 'stretch' | 'right';
}

const ExpandySection = (props: Props) => {
  const [expanded, setExpanded] = useState(props.initiallyExpanded || false);
  const isTransitioning = useRef(false);
  const [expandedHeight, setExpandedHeight] = useState<number | undefined>(
    props.initiallyExpanded ? undefined : 0,
  );
  const answerContainerRef = useRef<HTMLDivElement>(null);
  return (
    <div
      className={
        styles.container +
        ' ' +
        (props.containerClassName ? props.containerClassName : '') +
        ' ' +
        (expanded
          ? props.expandedContainerClassName
            ? props.expandedContainerClassName
            : ''
          : '') +
        ' ' +
        (props.expandButtonAlignment === 'right' ? styles.headerRight : '')
      }
      data-expandy-section-element
    >
      <button
        className={styles.header}
        onClick={(e) => {
          if (isTransitioning.current) {
            return;
          }
          isTransitioning.current = true;
          if (!expanded) {
            const answerContainer = e.currentTarget
              .closest('[data-expandy-section-element]')
              ?.querySelector('[data-expandy-section-children-element]') as
              | HTMLElement
              | undefined;
            if (answerContainer) {
              // Measure the expanded height of the container, and set that as the height to transition to. Once the
              // transition has ended, reset the height so that the container's height updates if the browser resizes
              const scrollHeight = answerContainer?.scrollHeight;
              const transitionEnd = () => {
                answerContainer.style.height = '';
                answerContainer.removeEventListener(
                  'transitionend',
                  transitionEnd,
                );
                isTransitioning.current = false;
              };
              answerContainer.addEventListener('transitionend', transitionEnd);
              setExpandedHeight(scrollHeight);
            } else {
              setExpandedHeight(undefined);
            }
          } else {
            // Set the height to the scrollHeight, so that the transition can occur when the expanded height is set back to zero
            const scrollHeight = e.currentTarget
              .closest('[data-expandy-section-element]')
              ?.querySelector(
                '[data-expandy-section-children-element]',
              )?.scrollHeight;
            if (scrollHeight && answerContainerRef.current) {
              const answerContainer = answerContainerRef.current;
              answerContainer.style.height = scrollHeight + 'px';
              prepareForTransition(answerContainerRef.current);
              const transitionEnd = () => {
                isTransitioning.current = false;
                answerContainer.removeEventListener(
                  'transitionend',
                  transitionEnd,
                );
              };
              answerContainer.addEventListener('transitionend', transitionEnd);
            }
            setExpandedHeight(0);
          }
          setExpanded(!expanded);
        }}
      >
        {props.headerContent}
      </button>

      <div
        ref={answerContainerRef}
        className={styles.childrenContainer}
        data-expandy-section-children-element
        style={{ height: expandedHeight }}
      >
        {props.children}
      </div>
    </div>
  );
};

export default ExpandySection;
